import { Link } from "react-router";
import { toTitleCase } from "./ActivitiesTableUtilFunctions";
import { trials } from "../trials";
import {
  COLUMN_WIDTHS,
  DEFAULT_HIDDEN_COLUMNS,
  STATUS_COLUMNS,
  COLUMNS_WITH_LINK,
  STRING_NO_TIMEPOINT_COLUMNS,
  NUMERIC_COLUMNS,
  NO_TIMEPOINT_COLUMNS,
  PROPEL_COLUMN_WIDTHS,
  NON_ENROLLED_STUDY_STATUSES,
} from "./VAConstants";
import {
  UNSCHEDULED_VISIT_PREFIX,
  getUnscheduledVisitPrefix,
  isUnscheduledVisit,
} from "../unscheduledVisitUtils/unscheduledVisitUtils";
import StatusPill from "../../components/UI/StatusPill";
import {
  cellStyleMap,
  iconTypeMap,
  textColor,
  pillColor,
  tooltipTitle,
} from "./VAStatusMapping";
import { CustomFilterIcon, CustomSortIcon } from "./GlobalFunctions";
import { Typography } from "antd";
const { Paragraph } = Typography;

function toSafeTestId(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function fractionToDecimal(
  fraction: string | null,
): number | string | null {
  if (fraction !== null && fraction.toString().includes("/")) {
    return fraction
      .split("/")
      .reduce(
        (numerator: number, denominator: string, i: number) =>
          numerator / (i ? Number(denominator) : 1),
        0 as unknown as number,
      );
  }
  return fraction;
}

export const getCellStyle = (
  text: string | null | undefined,
  type: string,
): React.CSSProperties | undefined => {
  if (text === null || text === undefined) text = "-";
  if (type === "completion") {
    return cellStyleMap[text] || cellStyleMap["default"];
  }
  if (type === "count") {
    const decimalValue = fractionToDecimal(text);
    const startsWith = text !== null ? text.toString().charAt(0) : null;
    if (decimalValue === 1) {
      //Value X/X
      return cellStyleMap["1"];
    }
    if (decimalValue === 0 || text === "0") {
      //Value 0/ or text = "0"
      return cellStyleMap["0"];
    }
    if (
      typeof decimalValue === "number" &&
      0 < decimalValue &&
      decimalValue < 1
    ) {
      //Value Y/X
      return cellStyleMap["fraction"];
    }
    if (text !== null && text.includes("-/")) {
      //Value -/X
      return cellStyleMap["-/"];
    }
    if (startsWith === "?") {
      //Value ?/X
      return cellStyleMap["?"];
    }
    if (text === "-") {
      //Value "Not Required" or "-" or ""
      return cellStyleMap["-"];
    }
    return cellStyleMap["default"];
  }
};

export const getFormattedTitles = (
  title: string,
  TITLE_MAPPING: Record<string, string>,
): React.ReactElement => {
  let newTitle = toTitleCase(title);
  const unscheduledPrefix = getUnscheduledVisitPrefix(title);

  if (TITLE_MAPPING[title]) newTitle = TITLE_MAPPING[title];
  if (unscheduledPrefix) {
    newTitle = `${unscheduledPrefix}${title.slice(unscheduledPrefix.length)}`;
  }

  return (
    <Paragraph
      style={{ margin: 0, color: "inherit" }}
      ellipsis={{ rows: 1, tooltip: newTitle }}
    >
      {newTitle}
    </Paragraph>
  );
};

export const getCellWidth = (header: string, trialId: string): number => {
  // if (isUnscheduledVisit(header)) {
  //   return COLUMN_WIDTHS.UNSCHEDULED;
  // }

  if (trialId === trials.bridgebio.PROPEL && PROPEL_COLUMN_WIDTHS[header]) {
    return PROPEL_COLUMN_WIDTHS[header];
  }

  return COLUMN_WIDTHS[header] || COLUMN_WIDTHS.DEFAULT;
};

export const getHiddenColumns = (
  header: string,
  columnToHide: string,
): boolean => {
  if (header.includes("_COMPLETION")) {
    return true;
  }
  if (columnToHide === UNSCHEDULED_VISIT_PREFIX) {
    if (DEFAULT_HIDDEN_COLUMNS.has(header) || isUnscheduledVisit(header)) {
      return true;
    }
  } else if (DEFAULT_HIDDEN_COLUMNS.has(header)) {
    return true;
  }
  return false;
};

export const getCellRenderer = (
  text: string | null | undefined,
  header: string,
  path: string,
  type: string,
  row: Record<string, unknown>,
): React.ReactNode => {
  if (text === null || text === undefined) text = "-";
  if (STATUS_COLUMNS.has(header)) {
    return (
      <StatusPill
        text={text}
        tooltipTitle={tooltipTitle}
        pillColor={pillColor}
        textColor={textColor}
      />
    );
  }

  if (COLUMNS_WITH_LINK.has(header)) {
    return (
      <Link style={{ color: "#06709B" }} to={`${path}/${text}`}>
        {text}
      </Link>
    );
  }
  // TIMEPOINTS: Check if the text is one of the keys in the iconTypeMap
  if (
    type === "completion" &&
    Object.hasOwn(iconTypeMap, row[`${header}_COMPLETION`] as PropertyKey)
  ) {
    return (
      <span
        data-testid={`visits-activities-cell-icon-${toSafeTestId(
          row.subject,
        )}-${toSafeTestId(header)}`}
      >
        {iconTypeMap[row[`${header}_COMPLETION`] as string]}
      </span>
    );
  }

  return text;
};

export const getSorter = (header: string) => {
  return (a: Record<string, unknown>, b: Record<string, unknown>): number => {
    if (a === null || a === undefined || b === null || b === undefined)
      return 0;
    // String columns without timepoints
    if (STRING_NO_TIMEPOINT_COLUMNS.has(header)) {
      const aValue = (a[header] as string) ?? "-";
      const bValue = (b[header] as string) ?? "-";
      return aValue.localeCompare(bValue);
    }

    // Numeric columns
    if (NUMERIC_COLUMNS.has(header)) {
      const aNum = parseFloat(a[header] as string);
      const bNum = parseFloat(b[header] as string);
      return aNum - bNum;
    }

    // Columns with timepoints

    if (!NO_TIMEPOINT_COLUMNS.has(header)) {
      if (
        (a[header] === null || a[header] === undefined) &&
        (b[header] === null || b[header] === undefined)
      )
        return 0;
      if (a[header] === null || a[header] === undefined) return -1;
      if (b[header] === null || b[header] === undefined) return 1;
      const [aNum] = String(a[header]).split("/").map(Number);
      const [bNum] = String(b[header]).split("/").map(Number);

      if (isNaN(aNum)) return -1;
      if (isNaN(bNum)) return 1;
      return aNum - bNum;
    }

    // Default case: handle both strings and numbers
    if (typeof a[header] === "string" && typeof b[header] === "string") {
      return (a[header] as string).localeCompare(b[header] as string);
    }
    return (a[header] as number) - (b[header] as number);
  };
};

export const createFilters = (
  rows: Record<string, unknown>[],
  header: string,
): { text: unknown; value: unknown }[] => {
  const columnValues = Array.from(
    new Set(
      rows.map((row) => (row[header] === "-" ? "Not Required" : row[header])),
    ),
  ).sort((a, b) => {
    a = a ?? "/";
    b = b ?? "/";

    if (!isNaN(a as number) && !isNaN(b as number)) {
      return (a as number) - (b as number);
    }

    if ((a as string)?.includes("/")) {
      const [aNum] = (typeof a === "string" ? a.split("/") : []).map(Number);
      const [bNum] = (typeof b === "string" ? b.split("/") : []).map(Number);
      if (isNaN(aNum)) return -1;
      if (isNaN(bNum)) return 1;
      return aNum - bNum;
    }
    return (a as string)?.localeCompare(b as string);
  });
  let completionValues: unknown[] = [];
  if (
    !header.includes("_COMPLETION") &&
    Object.keys(rows[0]).includes(`${header}_COMPLETION`)
  ) {
    completionValues = Array.from(
      new Set(rows.map((row) => row[`${header}_COMPLETION`])),
    ).sort((a, b) => {
      return (a as string)?.localeCompare(b as string);
    });
  }

  const allValues = [...completionValues, ...columnValues].map((value) => {
    if (value === "-" || value === null || value === undefined) {
      if (NO_TIMEPOINT_COLUMNS.has(header)) {
        return { text: "-", value: null };
      }

      return { text: "Not Required", value: "Not Required" };
    }
    return { text: value, value };
  });
  const uniqueValues = allValues.filter(
    (value, index, self) =>
      self.findIndex((t) => t.value === value.value) === index,
  );
  return uniqueValues;
};

/**
 * Filter values the Study Status column starts pre-selected with: every status
 * present in the data except the not-enrolled ones, so the grid opens on subjects
 * who actually made it into the trial.
 *
 * Returns undefined when there is nothing to hide, so untouched columns are not
 * shown as filtered. Values are stringified because that is how antd keys its
 * filter options (`String(filter.value)` in FilterDropdown).
 */
export const getDefaultFilteredValue = (
  header: string,
  filters: { text: unknown; value: unknown }[],
): string[] | undefined => {
  if (header !== "study_status") return undefined;

  const enrolled = filters
    .filter((filter) => !NON_ENROLLED_STUDY_STATUSES.has(String(filter.value)))
    .map((filter) => String(filter.value));

  if (enrolled.length === filters.length) return undefined;
  return enrolled;
};

export const getCellProperties = (
  record: Record<string, unknown>,
  header: string,
  handleCellClick: (
    header: string,
    value: unknown,
    record: Record<string, unknown>,
  ) => void,
  type: string,
): Record<string, unknown> => {
  const isUnscheduled = isUnscheduledVisit(header);

  if (NO_TIMEPOINT_COLUMNS.has(header)) {
    return {};
  }
  let render_header = header;
  if (type === "completion") {
    render_header = `${header}_COMPLETION`;
  }
  return {
    className: `cell-highlight ${isUnscheduled ? "unscheduled" : ""}`,
    "data-testid": `visits-activities-cell-${toSafeTestId(record.subject)}-${toSafeTestId(
      header,
    )}`,
    style: getCellStyle(
      record[render_header] as string | null | undefined,
      type,
    ),
    onClick: () =>
      handleCellClick(
        header,
        record[header] === "-" ? "Not Required" : record[header],
        record,
      ),
  };
};

export const getHeaderProperties = (header: string): Record<string, string> => {
  const isUnscheduled = isUnscheduledVisit(header);
  return {
    className: ` ${isUnscheduled ? "unscheduled-header" : ""}`,
  };
};

export const getFilterIcon = (isUnscheduled: boolean): React.ReactElement => (
  <CustomFilterIcon isUnscheduled={isUnscheduled} />
);

export const getSortIcon = (
  header: string,
  sortOrder: "ascend" | "descend" | null,
): React.ReactElement => (
  <CustomSortIcon
    isUnscheduled={isUnscheduledVisit(header)}
    order={sortOrder}
  />
);

const transformCountIntoCompletion = (value: string): string => {
  if (!value.includes("/") || value.includes("-")) {
    return "Not Required";
  }
  if (value.includes("/")) {
    const [completed, total] = value.split("/").map(Number);
    if (completed === 0) {
      return "No Activities Completed";
    }
    if (completed === total) {
      return "All Activities Completed";
    }
    return "Some Activities Completed";
  }

  return "No Data";
};

export const transformData = (
  data: Record<string, unknown>[] | null | undefined,
): Record<string, unknown>[] => {
  if (!data) return [];
  let maxKey = 0;
  const transformedData = data.map((row) => {
    Object.keys(row).forEach((key, i) => {
      if (i > maxKey) {
        maxKey = i;
      }
      if (!NO_TIMEPOINT_COLUMNS.has(key) && !key.includes("COMPLETION")) {
        row[`${key}_COMPLETION`] = transformCountIntoCompletion(
          row[key] as string,
        );
      }
    });
    return row;
  });

  return transformedData;
};
